/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

// Nussmart gift-cart presentation cleanup.
// The configurator intentionally keeps a technical base-lid child in the cart
// and fixes the legacy parent greeting-card option to "Ohne Grußkarte".
// Keep both data structures intact for inventory/variant logic, but suppress
// their customer-facing duplicate/contradictory display in cart and drawer.
//
// Hiding a whole row MUST go through the CSS class below. Dawn declares
// `display: grid` / `display: table-row` on `.cart-item` itself, and any
// author declaration outranks the user-agent rule `[hidden] { display: none }`
// — so the `hidden` attribute alone leaves the row visible. Single
// `.product-option` blocks carry no `display` declaration, which is why the
// greeting-card cleanup below can keep using `hidden` on its own.
const NM_HIDDEN_LINE_CLASS = 'nm-cart-line--hidden';
const NM_LID_PRODUCT_TITLE = 'deckelaufkleber für geschenkdosen';

function nmNormalize(value) {
  return (value || '').replace(/\s+/g, ' ').trim().toLocaleLowerCase('de-DE');
}

// setAttribute() queues a MutationRecord even when the value is unchanged,
// which would make the observer below re-run forever. Only write on change.
function nmSetAttribute(element, name, value) {
  if (value === null) {
    if (element.hasAttribute(name)) element.removeAttribute(name);
  } else if (element.getAttribute(name) !== value) {
    element.setAttribute(name, value);
  }
}

function nmReadCartLine(row) {
  const pairs = [...row.querySelectorAll('.cart-item__details .product-option')]
    .map((block) => ({
      block,
      name: nmNormalize(block.querySelector('dt')?.textContent).replace(/:$/, ''),
      value: nmNormalize(block.querySelector('dd')?.textContent),
    }))
    .filter((pair) => pair.name && pair.value);

  return {
    row,
    pairs,
    title: nmNormalize(row.querySelector('.cart-item__name')?.textContent),
  };
}

// `_nm_gift_group` and `_nm_gift_role` start with an underscore, so Dawn never
// renders them into the DOM. Group membership therefore has to be derived from
// the nested-line structure: Shopify emits children directly after their
// parent line, so a group is one non-nested row plus every following
// `.cart-item__nested-line` row.
function nmCollectGiftGroups(table) {
  const groups = [];

  for (const row of table.querySelectorAll('tr.cart-item')) {
    const line = nmReadCartLine(row);

    if (row.classList.contains('cart-item__nested-line')) {
      groups[groups.length - 1]?.children.push(line);
    } else {
      groups.push({ parent: line, children: [] });
    }
  }

  return groups;
}

function nmToggleLine(line, hidden) {
  line.row.classList.toggle(NM_HIDDEN_LINE_CLASS, hidden);
  line.row.toggleAttribute('hidden', hidden);
  nmSetAttribute(line.row, 'aria-hidden', hidden ? 'true' : null);
}

function cleanNussmartGiftCartPresentation() {
  for (const table of document.querySelectorAll('table.cart-items')) {
    for (const group of nmCollectGiftGroups(table)) {
      // Technical inventory child: the neutral base lid is always consumed,
      // even when a decorative motif is selected. Hide it only when a motif
      // child of the SAME gift box makes it redundant. A deliberate
      // "Ohne Motiv" order has no motif child and keeps its single,
      // customer-understandable line.
      const lidLines = group.children.filter((line) => line.title.includes(NM_LID_PRODUCT_TITLE));
      const motifOf = (line) => line.pairs.find((pair) => pair.name === 'motiv')?.value;
      const baseLid = lidLines.find((line) => motifOf(line) === 'ohne motiv');
      const decorativeLid = lidLines.find((line) => {
        const motif = motifOf(line);
        return motif && motif !== 'ohne motiv';
      });

      for (const line of lidLines) {
        nmToggleLine(line, line === baseLid && Boolean(decorativeLid));
      }

      // Configured gift parents contain the customer-facing Deckelmotiv line.
      // Their native variant option still says "Grußkarte: Ohne Grußkarte";
      // the configurator adds the real Grußkarte as a line-item property after
      // it. Hide only the technical "Ohne Grußkarte" occurrence when a second
      // greeting-card summary is present.
      const parentPairs = group.parent.pairs;
      if (!parentPairs.some((pair) => pair.name === 'deckelmotiv')) continue;

      const greetingPairs = parentPairs.filter((pair) => pair.name === 'grußkarte');
      if (greetingPairs.length < 2) continue;

      const technicalGreeting = greetingPairs.find((pair) => pair.value === 'ohne grußkarte');
      if (technicalGreeting && !technicalGreeting.block.hidden) {
        technicalGreeting.block.hidden = true;
      }
    }
  }
}

function initNussmartGiftCartPresentation() {
  cleanNussmartGiftCartPresentation();

  let cleanupQueued = false;
  const observer = new MutationObserver(() => {
    if (cleanupQueued) return;
    cleanupQueued = true;
    requestAnimationFrame(() => {
      cleanupQueued = false;
      cleanNussmartGiftCartPresentation();
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNussmartGiftCartPresentation, { once: true });
} else {
  initNussmartGiftCartPresentation();
}
